import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileWarning, Plus, Search, Filter, TrendingUp, TrendingDown, Clock,
  AlertTriangle, Users, DollarSign, FileText, ChevronRight, Calendar,
  Eye, Edit, Trash2, Send, Calculator, Brain, RefreshCw, Download, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Enums
const FASES = {
  EM_ANALISE: { label: 'Em Análise', color: 'bg-slate-500' },
  INSTAURACAO: { label: 'Instauração', color: 'bg-blue-500' },
  INSTRUCAO: { label: 'Instrução', color: 'bg-indigo-500' },
  DEFESA: { label: 'Defesa', color: 'bg-blue-500' },
  CONCLUSAO: { label: 'Conclusão', color: 'bg-amber-500' },
  JULGAMENTO: { label: 'Julgamento', color: 'bg-orange-500' },
  COBRANCA: { label: 'Cobrança', color: 'bg-red-500' },
  ENCERRADO: { label: 'Encerrado', color: 'bg-green-500' }
};

const ORIGENS = {
  ACHADO_AUDITORIA: 'Achado de Auditoria',
  AUDITORIA_INTERNA: 'Auditoria Interna',
  CONTROLE_INTERNO: 'Controle Interno',
  DENUNCIA: 'Denúncia',
  REPRESENTACAO: 'Representação',
  ATO_CONTROLE: 'Ato de Controle',
  INSPECAO: 'Inspeção',
  LEVANTAMENTO: 'Levantamento'
};

const TIPOS_DANO = {
  DESVIO: 'Desvio de Recursos',
  SUPERFATURAMENTO: 'Superfaturamento',
  INEXECUCAO: 'Inexecução Contratual',
  MAU_USO: 'Mau Uso de Recursos',
  FRAUDE: 'Fraude',
  OMISSAO: 'Omissão',
  PAGAMENTO_INDEVIDO: 'Pagamento Indevido',
  IRREGULARIDADE_LICITACAO: 'Irregularidade em Licitação',
  OUTRO: 'Outro'
};

const RISCOS = {
  BAIXO: { label: 'Baixo', color: 'bg-green-500' },
  MEDIO: { label: 'Médio', color: 'bg-yellow-500' },
  ALTO: { label: 'Alto', color: 'bg-orange-500' },
  CRITICO: { label: 'Crítico', color: 'bg-red-500' }
};

const TIPOS_RESPONSAVEL = {
  GESTOR: 'Gestor',
  ORDENADOR_DESPESA: 'Ordenador de Despesa',
  FISCAL_CONTRATO: 'Fiscal de Contrato',
  PREPOSTO: 'Preposto',
  TERCEIRIZADO: 'Terceirizado',
  SERVIDOR: 'Servidor',
  BENEFICIARIO: 'Beneficiário',
  OUTRO: 'Outro'
};

const GRAUS_RESPONSABILIDADE = {
  PRINCIPAL: 'Principal',
  SOLIDARIO: 'Solidário',
  SUBSIDIARIO: 'Subsidiário'
};

// Formatação de moeda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

// Formatação de data
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const TCEPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [filtros, setFiltros] = useState({ fase: '', origem: '', risco: '', busca: '' });
  
  // Modals
  const [showNovoProcesso, setShowNovoProcesso] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [processoSelecionado, setProcessoSelecionado] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const [calculos, setCalculos] = useState([]);
  const [andamentos, setAndamentos] = useState([]);
  const [insights, setInsights] = useState([]);
  
  // Form novo processo
  const [novoProcesso, setNovoProcesso] = useState({
    origem: 'AUDITORIA_INTERNA',
    orgao_entidade: '',
    objeto_dano: '',
    descricao_fato: '',
    tipo_dano: 'SUPERFATURAMENTO',
    valor_estimado: '',
    data_ocorrencia: '',
    numero_auditoria: '',
    fundamentacao_legal: 'Lei 8.443/1992; IN TCU 71/2012',
    responsaveis: []
  });
  
  // Novo responsável
  const [novoResponsavel, setNovoResponsavel] = useState({
    nome_completo: '',
    cpf_cnpj: '',
    cargo_funcao: '',
    vinculo_institucional: '',
    grau_responsabilidade: 'PRINCIPAL',
    tipo: 'SERVIDOR'
  });

  const token = localStorage.getItem('token');
  
  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pipelineRes, processosRes] = await Promise.all([
        api.get('/tce/dashboard/stats'),
        api.get('/tce/dashboard/pipeline'),
        api.get('/tce/processos')
      ]);
      setStats(statsRes.data);
      setPipeline(pipelineRes.data);
      setProcessos(processosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados TCE:', error);
      toast.error('Erro ao carregar dados do módulo TCE');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const carregarDetalhesProcesso = async (processo) => {
    try {
      const [respRes, calcRes, andRes, insRes] = await Promise.all([
        api.get(`/tce/processos/${processo.processo_id}/responsaveis`),
        api.get(`/tce/processos/${processo.processo_id}/calculos`),
        api.get(`/tce/processos/${processo.processo_id}/andamentos`),
        api.get(`/tce/processos/${processo.processo_id}/insights`)
      ]);
      setResponsaveis(respRes.data);
      setCalculos(calcRes.data);
      setAndamentos(andRes.data);
      setInsights(insRes.data);
      setProcessoSelecionado(processo);
      setShowDetalhes(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do processo');
    }
  };

  const criarProcesso = async () => {
    try {
      const payload = {
        ...novoProcesso,
        valor_estimado: parseFloat(novoProcesso.valor_estimado) || 0,
        data_ocorrencia: new Date(novoProcesso.data_ocorrencia).toISOString()
      };
      
      await api.post('/tce/processos', payload);
      toast.success('Processo TCE criado com sucesso!');
      setShowNovoProcesso(false);
      setNovoProcesso({
        origem: 'AUDITORIA_INTERNA',
        orgao_entidade: '',
        objeto_dano: '',
        descricao_fato: '',
        tipo_dano: 'SUPERFATURAMENTO',
        valor_estimado: '',
        data_ocorrencia: '',
        numero_auditoria: '',
        fundamentacao_legal: 'Lei 8.443/1992; IN TCU 71/2012',
        responsaveis: []
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao criar processo:', error);
      toast.error('Erro ao criar processo TCE');
    }
  };

  const adicionarResponsavelTemp = () => {
    if (!novoResponsavel.nome_completo || !novoResponsavel.cpf_cnpj) {
      toast.error('Nome e CPF/CNPJ são obrigatórios');
      return;
    }
    setNovoProcesso(prev => ({
      ...prev,
      responsaveis: [...prev.responsaveis, { ...novoResponsavel }]
    }));
    setNovoResponsavel({
      nome_completo: '',
      cpf_cnpj: '',
      cargo_funcao: '',
      vinculo_institucional: '',
      grau_responsabilidade: 'PRINCIPAL',
      tipo: 'SERVIDOR'
    });
    toast.success('Responsável adicionado');
  };

  const solicitarAnaliseIA = async (processoId) => {
    try {
      await api.post(`/tce/processos/${processoId}/ia/analisar`);
      toast.success('Análise IA solicitada. Aguarde os insights.');
      // Recarregar insights
      const insRes = await api.get(`/tce/processos/${processoId}/insights`);
      setInsights(insRes.data);
    } catch (error) {
      console.error('Erro na análise IA:', error);
      toast.error('Erro ao solicitar análise IA');
    }
  };

  const avancarFase = async (processoId, novaFase) => {
    try {
      await api.put(`/tce/processos/${processoId}`, { fase: novaFase });
      toast.success(`Processo movido para ${FASES[novaFase]?.label}`);
      carregarDados();
      if (processoSelecionado) {
        const procRes = await api.get(`/tce/processos/${processoId}`);
        setProcessoSelecionado(procRes.data);
        const andRes = await api.get(`/tce/processos/${processoId}/andamentos`);
        setAndamentos(andRes.data);
      }
    } catch (error) {
      console.error('Erro ao avançar fase:', error);
      toast.error('Erro ao avançar fase do processo');
    }
  };

  const processosFiltrados = processos.filter(p => {
    if (filtros.fase && p.fase !== filtros.fase) return false;
    if (filtros.origem && p.origem !== filtros.origem) return false;
    if (filtros.risco && p.nivel_risco !== filtros.risco) return false;
    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      return (
        p.numero_processo?.toLowerCase().includes(busca) ||
        p.orgao_entidade?.toLowerCase().includes(busca) ||
        p.objeto_dano?.toLowerCase().includes(busca)
      );
    }
    return true;
  });

  // Dashboard Tab
  const DashboardTab = () => (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total de Processos</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_processos || 0}</p>
              </div>
              <FileWarning className="w-10 h-10 text-blue-200" />
            </div>
            <div className="mt-4 flex gap-2 text-sm">
              <span className="bg-blue-500/30 px-2 py-1 rounded">{stats?.em_analise || 0} em análise</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-600 to-red-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Valor Total do Dano</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(stats?.valor_total_dano)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-red-200" />
            </div>
            <div className="mt-4 text-sm">
              <span className="text-red-200">Ressarcido: {formatCurrency(stats?.valor_total_ressarcido)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Taxa de Recuperação</p>
                <p className="text-3xl font-bold mt-1">{stats?.taxa_recuperacao?.toFixed(1) || 0}%</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-200" />
            </div>
            <div className="mt-4 text-sm">
              <span className="text-green-200">{stats?.encerrados || 0} processos encerrados</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-600 to-amber-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Alertas Críticos</p>
                <p className="text-3xl font-bold mt-1">{stats?.processos_criticos || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-200" />
            </div>
            <div className="mt-4 text-sm">
              <span className="text-amber-200">{stats?.processos_prescricao_proxima || 0} próx. prescrição</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5" />
            Pipeline de Processos
          </CardTitle>
          <CardDescription>Fluxo de tramitação dos processos TCE</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 overflow-x-auto pb-4">
            {pipeline.map((item, idx) => (
              <div key={item.fase} className="flex-shrink-0 w-40">
                <div className={`${FASES[item.fase]?.color} text-white p-3 rounded-t-lg text-center`}>
                  <p className="text-sm font-medium">{item.fase_label}</p>
                </div>
                <div className="bg-slate-100 p-4 rounded-b-lg text-center">
                  <p className="text-2xl font-bold text-slate-800">{item.quantidade}</p>
                  <p className="text-xs text-slate-500 mt-1">{formatCurrency(item.valor_total)}</p>
                </div>
                {idx < pipeline.length - 1 && (
                  <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Processos Recentes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Processos Recentes</CardTitle>
            <CardDescription>Últimos processos cadastrados</CardDescription>
          </div>
          <Button onClick={() => setActiveTab('processos')} variant="outline" size="sm">
            Ver todos <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {processos.slice(0, 5).map(processo => (
              <div 
                key={processo.processo_id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => carregarDetalhesProcesso(processo)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${RISCOS[processo.nivel_risco]?.color}`} />
                  <div>
                    <p className="font-medium text-slate-800">{processo.numero_processo}</p>
                    <p className="text-sm text-slate-500">{processo.orgao_entidade}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={FASES[processo.fase]?.color}>
                    {FASES[processo.fase]?.label}
                  </Badge>
                  <span className="text-sm font-medium text-slate-700">
                    {formatCurrency(processo.valor_apurado || processo.valor_estimado)}
                  </span>
                </div>
              </div>
            ))}
            {processos.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <FileWarning className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum processo TCE cadastrado</p>
                <Button className="mt-4" onClick={() => setShowNovoProcesso(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Criar Primeiro Processo
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Lista de Processos Tab
  const ProcessosTab = () => (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por número, órgão..."
                  value={filtros.busca}
                  onChange={e => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtros.fase || "all"} onValueChange={v => setFiltros(prev => ({ ...prev, fase: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Fase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Fases</SelectItem>
                {Object.entries(FASES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtros.risco || "all"} onValueChange={v => setFiltros(prev => ({ ...prev, risco: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Risco" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(RISCOS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setFiltros({ fase: '', origem: '', risco: '', busca: '' })} variant="outline">
              <Filter className="w-4 h-4 mr-2" /> Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <div className="space-y-3">
        {processosFiltrados.map(processo => (
          <Card key={processo.processo_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => carregarDetalhesProcesso(processo)}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  <div className={`w-2 h-full min-h-[80px] rounded-full ${RISCOS[processo.nivel_risco]?.color}`} />
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-800">{processo.numero_processo}</h3>
                      <Badge className={FASES[processo.fase]?.color}>{FASES[processo.fase]?.label}</Badge>
                      <Badge variant="outline">{ORIGENS[processo.origem]}</Badge>
                    </div>
                    <p className="text-slate-600 mt-1">{processo.orgao_entidade}</p>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{processo.objeto_dano}</p>
                    <div className="flex gap-4 mt-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {processo.total_responsaveis} responsáveis
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {formatDate(processo.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-800">{formatCurrency(processo.valor_apurado || processo.valor_estimado)}</p>
                  <p className="text-xs text-slate-500">Valor do Dano</p>
                  {processo.data_prescricao && (
                    <p className="text-xs text-amber-600 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Prescrição: {formatDate(processo.data_prescricao)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {processosFiltrados.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <FileWarning className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Nenhum processo encontrado com os filtros aplicados</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6" data-testid="tce-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Responsabilização e TCE</h1>
            <p className="text-slate-500">Tomada de Contas Especial - Lei 8.443/1992</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={carregarDados}>
              <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
            </Button>
            <Button onClick={() => setShowNovoProcesso(true)} className="bg-[#1d4ed8] hover:bg-[#5a35e0]">
              <Plus className="w-4 h-4 mr-2" /> Novo Processo
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-white">Dashboard</TabsTrigger>
            <TabsTrigger value="processos" className="data-[state=active]:bg-white">Processos ({processos.length})</TabsTrigger>
            <TabsTrigger value="relatorios" className="data-[state=active]:bg-white">Relatórios</TabsTrigger>
            <TabsTrigger value="configuracoes" className="data-[state=active]:bg-white">Configurações</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-[#1d4ed8] border-t-transparent rounded-full" />
              </div>
            ) : (
              <DashboardTab />
            )}
          </TabsContent>

          <TabsContent value="processos" className="mt-6">
            <ProcessosTab />
          </TabsContent>

          <TabsContent value="relatorios" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Relatórios Disponíveis</CardTitle>
                <CardDescription>Gere relatórios oficiais para a Corte de Contas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {['Relatório de Instauração', 'Relatório de Instrução', 'Relatório de Dano', 'Relatório Conclusivo', 'Laudo de Atualização Monetária', 'Peça de Cobrança'].map(tipo => (
                    <Card key={tipo} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-[#1d4ed8]" />
                          <div>
                            <p className="font-medium">{tipo}</p>
                            <p className="text-xs text-slate-500">Selecione um processo para gerar</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="configuracoes" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Configurações do Módulo</CardTitle>
                <CardDescription>Parametrize prazos, índices e templates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-700">Prazos Legais</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Prescrição (anos)</span>
                        <Input type="number" className="w-20" defaultValue={5} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Citação (dias)</span>
                        <Input type="number" className="w-20" defaultValue={15} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Defesa (dias)</span>
                        <Input type="number" className="w-20" defaultValue={15} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Recurso (dias)</span>
                        <Input type="number" className="w-20" defaultValue={15} />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-700">Cálculo de Dano</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Índice de Atualização</span>
                        <Select defaultValue="SELIC">
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SELIC">SELIC</SelectItem>
                            <SelectItem value="IPCA">IPCA</SelectItem>
                            <SelectItem value="INPC">INPC</SelectItem>
                            <SelectItem value="IGP_M">IGP-M</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Juros de Mora (%/mês)</span>
                        <Input type="number" className="w-20" defaultValue={1.0} step={0.1} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Multa de Atraso (%)</span>
                        <Input type="number" className="w-20" defaultValue={2.0} step={0.1} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button className="bg-[#1d4ed8] hover:bg-[#5a35e0]">Salvar Configurações</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal Novo Processo */}
        <Dialog open={showNovoProcesso} onOpenChange={setShowNovoProcesso}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Processo de TCE</DialogTitle>
              <DialogDescription>Registre um novo processo de Tomada de Contas Especial</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Dados do Processo */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700 border-b pb-2">Dados do Processo</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Origem *</Label>
                    <Select value={novoProcesso.origem} onValueChange={v => setNovoProcesso(prev => ({ ...prev, origem: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ORIGENS).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de Dano *</Label>
                    <Select value={novoProcesso.tipo_dano} onValueChange={v => setNovoProcesso(prev => ({ ...prev, tipo_dano: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIPOS_DANO).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Órgão/Entidade *</Label>
                  <Input
                    value={novoProcesso.orgao_entidade}
                    onChange={e => setNovoProcesso(prev => ({ ...prev, orgao_entidade: e.target.value }))}
                    placeholder="Ex: Secretaria de Saúde"
                  />
                </div>
                
                <div>
                  <Label>Objeto do Dano *</Label>
                  <Input
                    value={novoProcesso.objeto_dano}
                    onChange={e => setNovoProcesso(prev => ({ ...prev, objeto_dano: e.target.value }))}
                    placeholder="Ex: Contrato de Fornecimento de Medicamentos"
                  />
                </div>
                
                <div>
                  <Label>Descrição dos Fatos *</Label>
                  <Textarea
                    value={novoProcesso.descricao_fato}
                    onChange={e => setNovoProcesso(prev => ({ ...prev, descricao_fato: e.target.value }))}
                    placeholder="Descreva detalhadamente os fatos que deram origem à TCE..."
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Estimado do Dano (R$) *</Label>
                    <Input
                      type="number"
                      value={novoProcesso.valor_estimado}
                      onChange={e => setNovoProcesso(prev => ({ ...prev, valor_estimado: e.target.value }))}
                      placeholder="0,00"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>Data da Ocorrência *</Label>
                    <Input
                      type="date"
                      value={novoProcesso.data_ocorrencia}
                      onChange={e => setNovoProcesso(prev => ({ ...prev, data_ocorrencia: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Número da Auditoria</Label>
                    <Input
                      value={novoProcesso.numero_auditoria}
                      onChange={e => setNovoProcesso(prev => ({ ...prev, numero_auditoria: e.target.value }))}
                      placeholder="Ex: AUD-2024-0042"
                    />
                  </div>
                  <div>
                    <Label>Fundamentação Legal</Label>
                    <Input
                      value={novoProcesso.fundamentacao_legal}
                      onChange={e => setNovoProcesso(prev => ({ ...prev, fundamentacao_legal: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Responsáveis */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700 border-b pb-2">Responsáveis</h4>
                
                {novoProcesso.responsaveis.length > 0 && (
                  <div className="space-y-2">
                    {novoProcesso.responsaveis.map((resp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{resp.nome_completo}</p>
                          <p className="text-sm text-slate-500">{resp.cpf_cnpj} - {TIPOS_RESPONSAVEL[resp.tipo]}</p>
                        </div>
                        <Badge>{GRAUS_RESPONSABILIDADE[resp.grau_responsabilidade]}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="p-4 border-2 border-dashed rounded-lg space-y-3">
                  <p className="text-sm text-slate-500 font-medium">Adicionar Responsável</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Nome Completo *"
                      value={novoResponsavel.nome_completo}
                      onChange={e => setNovoResponsavel(prev => ({ ...prev, nome_completo: e.target.value }))}
                    />
                    <Input
                      placeholder="CPF/CNPJ *"
                      value={novoResponsavel.cpf_cnpj}
                      onChange={e => setNovoResponsavel(prev => ({ ...prev, cpf_cnpj: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Cargo/Função"
                      value={novoResponsavel.cargo_funcao}
                      onChange={e => setNovoResponsavel(prev => ({ ...prev, cargo_funcao: e.target.value }))}
                    />
                    <Input
                      placeholder="Vínculo Institucional"
                      value={novoResponsavel.vinculo_institucional}
                      onChange={e => setNovoResponsavel(prev => ({ ...prev, vinculo_institucional: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={novoResponsavel.tipo} onValueChange={v => setNovoResponsavel(prev => ({ ...prev, tipo: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIPOS_RESPONSAVEL).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={novoResponsavel.grau_responsabilidade} onValueChange={v => setNovoResponsavel(prev => ({ ...prev, grau_responsabilidade: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Grau" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(GRAUS_RESPONSABILIDADE).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" className="w-full" onClick={adicionarResponsavelTemp}>
                    <Plus className="w-4 h-4 mr-2" /> Adicionar Responsável
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNovoProcesso(false)}>Cancelar</Button>
              <Button onClick={criarProcesso} className="bg-[#1d4ed8] hover:bg-[#5a35e0]">
                Criar Processo TCE
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Detalhes do Processo */}
        <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {processoSelecionado?.numero_processo}
                <Badge className={FASES[processoSelecionado?.fase]?.color}>
                  {FASES[processoSelecionado?.fase]?.label}
                </Badge>
                <Badge className={RISCOS[processoSelecionado?.nivel_risco]?.color}>
                  Risco {RISCOS[processoSelecionado?.nivel_risco]?.label}
                </Badge>
              </DialogTitle>
              <DialogDescription>{processoSelecionado?.orgao_entidade}</DialogDescription>
            </DialogHeader>

            {processoSelecionado && (
              <Tabs defaultValue="info" className="mt-4">
                <TabsList>
                  <TabsTrigger value="info">Informações</TabsTrigger>
                  <TabsTrigger value="responsaveis">Responsáveis ({responsaveis.length})</TabsTrigger>
                  <TabsTrigger value="calculos">Cálculos ({calculos.length})</TabsTrigger>
                  <TabsTrigger value="andamentos">Andamentos ({andamentos.length})</TabsTrigger>
                  <TabsTrigger value="ia">IA Insights ({insights.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Objeto do Dano</Label>
                      <p className="font-medium">{processoSelecionado.objeto_dano}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Tipo de Dano</Label>
                      <p className="font-medium">{TIPOS_DANO[processoSelecionado.tipo_dano]}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Valor Estimado</Label>
                      <p className="font-medium text-lg">{formatCurrency(processoSelecionado.valor_estimado)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Valor Apurado</Label>
                      <p className="font-medium text-lg text-red-600">{formatCurrency(processoSelecionado.valor_apurado)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Data da Ocorrência</Label>
                      <p className="font-medium">{formatDate(processoSelecionado.data_ocorrencia)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Prescrição</Label>
                      <p className="font-medium text-amber-600">{formatDate(processoSelecionado.data_prescricao)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-500">Descrição dos Fatos</Label>
                    <p className="mt-1 text-slate-700 bg-slate-50 p-3 rounded-lg">{processoSelecionado.descricao_fato}</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button onClick={() => solicitarAnaliseIA(processoSelecionado.processo_id)} variant="outline">
                      <Brain className="w-4 h-4 mr-2" /> Solicitar Análise IA
                    </Button>
                    {processoSelecionado.fase !== 'ENCERRADO' && (
                      <Select onValueChange={v => avancarFase(processoSelecionado.processo_id, v)}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Avançar para..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FASES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="responsaveis" className="mt-4">
                  <div className="space-y-3">
                    {responsaveis.map(resp => (
                      <Card key={resp.responsavel_id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{resp.nome_completo}</p>
                              <p className="text-sm text-slate-500">{resp.cpf_cnpj}</p>
                              <p className="text-sm text-slate-500">{resp.cargo_funcao} - {resp.vinculo_institucional}</p>
                            </div>
                            <div className="text-right">
                              <Badge className="mb-2">{GRAUS_RESPONSABILIDADE[resp.grau_responsabilidade]}</Badge>
                              <p className="text-sm">Valor Imputado: <span className="font-medium">{formatCurrency(resp.valor_imputado)}</span></p>
                              <p className="text-sm">Ressarcido: <span className="font-medium text-green-600">{formatCurrency(resp.valor_ressarcido)}</span></p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="calculos" className="mt-4">
                  <div className="space-y-3">
                    {calculos.map(calc => (
                      <Card key={calc.calculo_id}>
                        <CardContent className="p-4">
                          <div className="grid grid-cols-4 gap-4 text-center">
                            <div>
                              <p className="text-sm text-slate-500">Valor Original</p>
                              <p className="font-semibold">{formatCurrency(calc.valor_original)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Atualizado</p>
                              <p className="font-semibold">{formatCurrency(calc.valor_atualizado)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Juros + Multa</p>
                              <p className="font-semibold">{formatCurrency(calc.juros + calc.multa)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Total Devido</p>
                              <p className="font-bold text-red-600 text-lg">{formatCurrency(calc.total_devido)}</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2 text-center">
                            Metodologia: {calc.metodologia} | Calculado em: {formatDate(calc.data_calculo)}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                    {calculos.length === 0 && (
                      <p className="text-center text-slate-500 py-4">Nenhum cálculo de dano registrado</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="andamentos" className="mt-4">
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                    <div className="space-y-4">
                      {andamentos.map((and, idx) => (
                        <div key={and.andamento_id} className="relative pl-10">
                          <div className={`absolute left-2 w-4 h-4 rounded-full ${FASES[and.fase_atual]?.color} ring-4 ring-white`} />
                          <Card>
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{and.descricao}</p>
                                  <p className="text-sm text-slate-500">{and.usuario_nome}</p>
                                </div>
                                <div className="text-right">
                                  <Badge className={FASES[and.fase_atual]?.color}>{FASES[and.fase_atual]?.label}</Badge>
                                  <p className="text-xs text-slate-400 mt-1">{formatDate(and.created_at)}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="ia" className="mt-4">
                  <div className="space-y-3">
                    {insights.map(ins => (
                      <Card key={ins.insight_id} className={ins.aplicado ? 'opacity-50' : ''}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Brain className="w-5 h-5 text-[#1d4ed8] flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <p className="font-medium">{ins.titulo}</p>
                                <Badge variant="outline">{(ins.confianca * 100).toFixed(0)}% confiança</Badge>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{ins.conteudo}</p>
                              {ins.acao_sugerida && (
                                <p className="text-sm text-[#1d4ed8] mt-2 font-medium">Sugestão: {ins.acao_sugerida}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {insights.length === 0 && (
                      <div className="text-center py-8">
                        <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="text-slate-500">Nenhum insight disponível</p>
                        <Button className="mt-4" variant="outline" onClick={() => solicitarAnaliseIA(processoSelecionado.processo_id)}>
                          Solicitar Análise IA
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default TCEPage;
